import {
  DataScope,
  isFirmAssignableRole,
  isTrustAccountGuestRole,
  isTrustAccountWriteAction,
  POWER_OF_ATTORNEY_ACTIONS,
  SYSTEM_ROLE_DEFINITIONS,
  TRUST_ACCOUNT_GUEST_ROLE_PERMISSIONS,
  TRUST_ACCOUNT_MEMBER_ROLE_PERMISSIONS,
  TrustAccountMemberIneligibleReason
} from '@sollapay/enums';

import {TrustAccountGrantRoleMismatchError} from '../errors';

import type {
  Role,
  TrustAccountAction,
  TrustAccountGrantRole,
  TrustAccountMemberRole
} from '@sollapay/enums';

/**
 * Why a firm role can't hold a member role on a trust account, or `null` when it can. Derived from
 * the firm role's data scopes and what the account role grants: a membership only grants access the
 * firm role doesn't already have firm-wide.
 * @param params.firmRoleKey - The candidate's firm role key
 * @param params.memberRole - The requested per-account role
 */
export function getTrustAccountMemberIneligibleReason(params: {
  firmRoleKey: string;
  memberRole: TrustAccountMemberRole;
}): TrustAccountMemberIneligibleReason | null {
  const definition = SYSTEM_ROLE_DEFINITIONS[params.firmRoleKey as Role];
  if (!definition || !isFirmAssignableRole(params.firmRoleKey)) {
    return TrustAccountMemberIneligibleReason.NOT_ASSIGNABLE;
  }

  const grantsWrites =
    TRUST_ACCOUNT_MEMBER_ROLE_PERMISSIONS[params.memberRole].some(isTrustAccountWriteAction);
  if (!grantsWrites) {
    return definition.readScope === DataScope.FIRM
      ? TrustAccountMemberIneligibleReason.ALREADY_READS_ALL
      : null;
  }

  if (definition.writeScope === null) return TrustAccountMemberIneligibleReason.NO_WRITE_ACCESS;
  if (definition.writeScope === DataScope.FIRM) {
    return TrustAccountMemberIneligibleReason.ALREADY_WRITES_ALL;
  }
  return null;
}

/**
 * What a member or guest may do on the account: their role's permissions, without the actions taken
 * in the trustee's name unless the trustee granted a member power of attorney. Guest roles never
 * act in the trustee's name, so power of attorney is ignored for them.
 */
export function resolveTrustAccountMemberPermissions(params: {
  role: TrustAccountGrantRole;
  hasPowerOfAttorney: boolean;
}): readonly TrustAccountAction[] {
  if (isTrustAccountGuestRole(params.role))
    return TRUST_ACCOUNT_GUEST_ROLE_PERMISSIONS[params.role];
  const permissions = TRUST_ACCOUNT_MEMBER_ROLE_PERMISSIONS[params.role];
  if (params.hasPowerOfAttorney) return permissions;
  return permissions.filter(action => !POWER_OF_ATTORNEY_ACTIONS.includes(action));
}

/** Whether holding the role means acting in the trustee's name — making someone one needs power of attorney. */
export function memberRoleActsForTrustee(role: TrustAccountMemberRole): boolean {
  return TRUST_ACCOUNT_MEMBER_ROLE_PERMISSIONS[role].some(action =>
    POWER_OF_ATTORNEY_ACTIONS.includes(action)
  );
}

/** Narrows a grant role read from a member-only query; a guest role there is a broken invariant. */
export function toMemberRole(role: TrustAccountGrantRole): TrustAccountMemberRole {
  if (isTrustAccountGuestRole(role)) {
    throw new TrustAccountGrantRoleMismatchError(role, 'member');
  }
  return role;
}
